import { useRef, useState } from 'react';
import { dismiss, notifyProblems } from '../lib/errorNotices';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import {
  IMPORT_ENTITIES,
  IMPORT_ENTITY_LABELS,
  IMPORT_ENTITY_NOTES,
  MAX_IMPORT_BYTES,
  MAX_IMPORT_ROWS,
  autoMap,
  canCutOver,
  canManageProperties,
  mappingIssues,
  parseCsv,
  planImport,
  type ImportEntity,
  type Mapping,
} from '@promanage/core';
import { Button, Chip, Panel, PanelHeader } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useSession } from '../features/auth/session';
import { ColumnMap } from '../features/import/ColumnMap';
import { Issues } from '../features/import/Issues';
import { RequiredFields } from '../features/import/RequiredFields';
import { CutoverCard } from '../features/import/CutoverCard';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/settings/import')({
  component: ImportPage,
});

/**
 * Bringing an agency's book across from whatever they are leaving.
 *
 * FOUR STEPS, IN ORDER, and each one only appears once the one before it is answered: what
 * kind of record, which file, which column is which, then check and import. A wizard is
 * usually the wrong shape for this product — CLAUDE.md §1, "the operator is an expert" — but
 * this screen is used a handful of times in an agency's life, by somebody who has never seen
 * it before, on the day their data is at stake. That is the one case where being led is right.
 *
 * THE IMPORT LANDS WHOLE OR NOT AT ALL. Said on the screen, twice, because it is the fact that
 * makes it safe to press the button: there is no state where half a portfolio arrived and the
 * operator has to work out which half.
 *
 * The order the entities are listed in is the order they have to be imported in — owners hold
 * the money, properties belong to owners, tenancies let properties, and insurance and smoke
 * alarms are facts about a property that has to exist first. The list is not sorted
 * alphabetically for exactly that reason. Contractors are the one kind that depends on nothing,
 * and they sit after the chain they are not part of rather than interrupting it.
 */

/** One notice for the chosen file's own problems, replaced when another file is chosen. */
const READ_PROBLEM = 'import-file';
function ImportPage() {
  const session = useSession();
  const mayImport = canManageProperties(session?.roles ?? []);
  const mayCutOver = canCutOver(session?.roles ?? []);
  /** The money comes after every kind of record — the last step of the same journey. */
  const cutoverStep = IMPORT_ENTITIES.length + 1;

  const [entity, setEntity] = useState<ImportEntity>('owners');
  const [file, setFile] = useState<{ name: string; csv: string } | null>(null);
  const [mapping, setMapping] = useState<Mapping>({});
  const queryClient = useQueryClient();

  const preview = useMutation(
    trpc.import.preview.mutationOptions({ meta: { errorTitle: 'Could not check that file' } }),
  );
  const commit = useMutation(
    trpc.import.commit.mutationOptions({
      meta: { errorTitle: 'Nothing was imported' },
      onSuccess() {
        // Everything downstream of an import is now wrong: the owner list, the portfolio, the
        // people list, the dashboard's counts. Clearing the lot is blunt and correct — this
        // happens once, and a stale property list after a migration reads as a failed import.
        void queryClient.invalidateQueries();
      },
    }),
  );

  /** Parsed here, in the browser, with the same parser the server will use. */
  const table = file ? parseCsv(file.csv) : null;
  const mapProblems = table ? mappingIssues(entity, mapping, table.headers.length) : [];

  /*
   * The instant answer, from `@promanage/core` — the same planner the server runs.
   *
   * It cannot see the database, so it knows nothing about whether owner "OW000003" exists. What
   * it does know is every rule the file can answer for itself, and it knows it the moment a
   * column is re-mapped rather than after a round trip. The server's answer is still the one
   * that decides; this is what makes mapping feel like editing rather than submitting.
   */
  const local = table && mapProblems.length === 0 ? planImport({ entity, table, mapping }) : null;

  /** A preview stops meaning anything the moment any of its three inputs change. */
  const checked = preview.data !== undefined && !preview.isPending;

  const reset = (next?: Partial<{ entity: ImportEntity; file: typeof file }>) => {
    preview.reset();
    commit.reset();
    if (next?.entity !== undefined) {
      setEntity(next.entity);
      setFile(null);
      setMapping({});
    }
    if (next?.file !== undefined) {
      setFile(next.file);
      setMapping(next.file ? autoMap(entity, parseCsv(next.file.csv).headers) : {});
    }
  };

  /*
   * Reading the file is async; a change handler is not.
   *
   * Written as a void-returning function that starts the read rather than as an `async`
   * handler, because an unhandled rejection from an event attribute goes nowhere the operator
   * can see it. A file that cannot be read says so in the same place a file that is too large
   * does.
   */
  const onFile = (chosen: File | undefined): void => {
    if (!chosen) return;
    dismiss(READ_PROBLEM);

    if (chosen.size > MAX_IMPORT_BYTES) {
      notifyProblems(
        'Could not read that file',
        [
          `That file is ${Math.round(chosen.size / 1024)}KB and the limit is ${Math.round(MAX_IMPORT_BYTES / 1024)}KB. Split it and import the halves — each one lands whole.`,
        ],
        READ_PROBLEM,
      );
      return;
    }

    chosen
      .text()
      .then((csv) => reset({ file: { name: chosen.name, csv } }))
      .catch(() =>
        notifyProblems(
          'Could not read that file',
          ['That file could not be read. Save it again as CSV and retry.'],
          READ_PROBLEM,
        ),
      );
  };

  if (!mayImport) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Import" />
        <SettingsNav />
        <Panel className="p-6">
          <h2 className="text-[1.125rem] font-bold">Importing needs a property management role</h2>
          <p className="mt-1 max-w-prose text-[0.875rem] text-ink-muted">
            An import creates owners, properties, tenancies, people, contractors and a
            property&rsquo;s compliance records in bulk. Ask an administrator to give you a property
            management role, or to run the import for you.
          </p>
        </Panel>
        {/* A trust administrator without a property role still runs the money step. */}
        {mayCutOver ? (
          <div className="mt-4">
            <CutoverCard step={cutoverStep} canRun />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Import"
        subtitle="Bring an agency's book across from a spreadsheet — owners, properties, tenancies, people, contractors and compliance"
      />

      <SettingsNav />

      <div className="flex flex-col gap-4">
        {/* ── 1. What kind of record ──────────────────────────────────────────── */}
        <Panel className="p-0">
          <PanelHeader
            title="What are you importing?"
            subtitle="One kind at a time. The order is what depends on what — a property needs its owner, a policy needs its property."
          />
          <div className="grid gap-2 border-t border-rule px-5 py-4 sm:grid-cols-2">
            {IMPORT_ENTITIES.map((option, index) => (
              <button
                key={option}
                type="button"
                aria-pressed={entity === option}
                onClick={() => reset({ entity: option })}
                className={`rounded-lg px-4 py-3 text-left ring-1 transition-colors duration-[var(--duration-fast)] ${
                  entity === option
                    ? 'bg-accent-soft ring-accent'
                    : 'bg-surface ring-rule hover:bg-surface-sunk'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="flex size-5 shrink-0 items-center justify-center rounded-full bg-surface-sunk text-[0.6875rem] font-bold text-ink-muted"
                  >
                    {index + 1}
                  </span>
                  <span className="text-[0.9375rem] font-bold">{IMPORT_ENTITY_LABELS[option]}</span>
                </span>
                <span className="mt-1 block text-[0.75rem] text-ink-muted">
                  {IMPORT_ENTITY_NOTES[option]}
                </span>
              </button>
            ))}
          </div>
        </Panel>

        <CutoverCard step={cutoverStep} canRun={mayCutOver} />

        <RequiredFields entity={entity} />

        {/* ── 2. The file ─────────────────────────────────────────────────────── */}
        <ChooseFile
          entity={entity}
          file={file}
          rowCount={table?.records.length ?? 0}
          columnCount={table?.headers.length ?? 0}
          onChoose={onFile}
          onClear={() => reset({ file: null })}
        />

        {/* ── 3. The mapping ──────────────────────────────────────────────────── */}
        {table && table.headers.length > 0 ? (
          <>
            <ColumnMap
              entity={entity}
              headers={table.headers}
              firstRow={table.records[0]?.cells ?? []}
              mapping={mapping}
              onChange={(next) => {
                setMapping(next);
                // The previous answer described a different mapping. Keeping it on screen while
                // the operator edits would let them press Import on a check that no longer holds.
                preview.reset();
                commit.reset();
              }}
              problemFields={
                new Set(
                  (preview.data?.issues ?? []).flatMap((i) => (i.field === null ? [] : [i.field])),
                )
              }
            />

            {mapProblems.length > 0 ? (
              <Panel className="p-5">
                <h2 className="text-[0.9375rem] font-bold">Finish the mapping first</h2>
                <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-[0.8125rem] text-ink-muted">
                  {mapProblems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </Panel>
            ) : null}

            {/* ── 4. Check, then import ──────────────────────────────────────── */}
            {mapProblems.length === 0 ? (
              <Panel className="p-5">
                <PanelHeader
                  title="Check the file"
                  subtitle="Reads every row and looks up every owner, property and email it names. Nothing is written."
                  className="p-0 pb-3"
                />

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    onClick={() => file && preview.mutate({ entity, csv: file.csv, mapping })}
                    disabled={preview.isPending || !file}
                  >
                    {preview.isPending ? 'Checking' : 'Check the file'}
                  </Button>

                  {local ? (
                    <span className="text-[0.8125rem] text-ink-muted">
                      {local.records.length} record{local.records.length === 1 ? '' : 's'} from{' '}
                      {local.rowCount} row{local.rowCount === 1 ? '' : 's'}
                      {local.issues.length > 0
                        ? ` — ${local.issues.length} problem${local.issues.length === 1 ? '' : 's'} in the file itself`
                        : ''}
                    </span>
                  ) : null}
                </div>

                {checked && preview.data ? (
                  <Result
                    ready={preview.data.ready}
                    recordCount={preview.data.recordCount}
                    rowCount={preview.data.rowCount}
                    sample={preview.data.sample}
                    entity={entity}
                    onImport={() =>
                      file &&
                      commit.mutate({
                        entity,
                        csv: file.csv,
                        mapping,
                        expectedRecords: preview.data.recordCount,
                      })
                    }
                    importing={commit.isPending}
                    imported={commit.data ?? null}
                  />
                ) : null}
              </Panel>
            ) : null}

            {checked && preview.data && preview.data.issues.length > 0 ? (
              <Issues issues={preview.data.issues} />
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

// ── The file ─────────────────────────────────────────────────────────────────

function ChooseFile({
  entity,
  file,
  rowCount,
  columnCount,
  onChoose,
  onClear,
}: {
  entity: ImportEntity;
  file: { name: string; csv: string } | null;
  rowCount: number;
  columnCount: number;
  onChoose: (file: File | undefined) => void;
  onClear: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  return (
    <Panel className="p-0">
      <PanelHeader
        title="Choose the file"
        subtitle={`A CSV with a header row, up to ${MAX_IMPORT_ROWS.toLocaleString('en-NZ')} rows. Commas, semicolons and tabs are all read.`}
        aside={
          file ? (
            <Button size="sm" variant="ghost" onClick={onClear}>
              Choose another
            </Button>
          ) : null
        }
      />

      <div className="border-t border-rule px-5 py-4">
        {/*
         * A drop target, not a control. The button inside it is the control, and it is what a
         * keyboard and a screen reader use — dragging a file is a pointer-only convenience
         * layered on top, so this div deliberately has no role and no tabindex.
         */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            onChoose(e.dataTransfer.files[0]);
          }}
          className={`flex flex-col items-start gap-2 rounded-lg border border-dashed px-4 py-5 transition-colors duration-[var(--duration-fast)] ${
            over ? 'border-accent bg-accent-soft' : 'border-rule bg-surface-sunk'
          }`}
        >
          <input
            ref={input}
            type="file"
            accept=".csv,text/csv,text/plain"
            className="sr-only"
            onChange={(e) => onChoose(e.target.files?.[0])}
          />

          {file ? (
            <div className="flex flex-wrap items-center gap-2">
              <Chip tone="positive">{file.name}</Chip>
              <span className="text-[0.8125rem] text-ink-muted">
                {rowCount.toLocaleString('en-NZ')} row{rowCount === 1 ? '' : 's'}, {columnCount}{' '}
                column{columnCount === 1 ? '' : 's'}
              </span>
            </div>
          ) : (
            <p className="text-[0.8125rem] text-ink-muted">
              Drop your {IMPORT_ENTITY_LABELS[entity].toLowerCase()} file here, or
            </p>
          )}

          <Button size="sm" variant="secondary" onClick={() => input.current?.click()}>
            {file ? 'Choose a different file' : 'Choose a file'}
          </Button>
        </div>

        {file && columnCount === 0 ? (
          <p
            role="alert"
            className="mt-3 rounded-md bg-danger-soft px-3 py-2 text-[0.8125rem] font-medium text-danger"
          >
            That file has no header row. The first line has to name the columns.
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

// ── The verdict ──────────────────────────────────────────────────────────────

function Result({
  ready,
  recordCount,
  rowCount,
  sample,
  entity,
  onImport,
  importing,
  imported,
}: {
  ready: boolean;
  recordCount: number;
  rowCount: number;
  sample: readonly string[];
  entity: ImportEntity;
  onImport: () => void;
  importing: boolean;
  imported: { created: number; sample: string[] } | null;
}) {
  if (imported) {
    return (
      <div className="mt-4 rounded-lg bg-positive-soft px-4 py-3 ring-1 ring-positive/25">
        <p className="text-[0.9375rem] font-bold text-positive">
          {imported.created} {IMPORT_ENTITY_LABELS[entity].toLowerCase()} imported.
        </p>
        <ul className="mt-2 flex flex-col gap-0.5 text-[0.8125rem] text-ink-muted">
          {imported.sample.map((line) => (
            <li key={line}>{line}</li>
          ))}
          {imported.created > imported.sample.length ? (
            <li>…and {imported.created - imported.sample.length} more.</li>
          ) : null}
        </ul>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {ready ? <Chip tone="positive">Ready</Chip> : <Chip tone="danger">Not yet</Chip>}
        <span className="text-[0.8125rem] text-ink-muted">
          {recordCount} {IMPORT_ENTITY_LABELS[entity].toLowerCase()} from{' '}
          {rowCount.toLocaleString('en-NZ')} row{rowCount === 1 ? '' : 's'}
        </span>
      </div>

      {ready && sample.length > 0 ? (
        <div className="rounded-md bg-surface-sunk px-3 py-2">
          <p className="text-[0.75rem] font-semibold text-ink-muted">
            The first few, so you can recognise your own data:
          </p>
          <ul className="mt-1 flex flex-col gap-0.5 text-[0.8125rem]">
            {sample.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {ready ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button size="lg" onClick={onImport} disabled={importing}>
            {importing
              ? 'Importing'
              : `Import ${recordCount} record${recordCount === 1 ? '' : 's'}`}
          </Button>
          <p className="text-[0.75rem] text-ink-subtle">
            All {recordCount} land together, or none do. It is recorded in the audit log with who
            ran it.
          </p>
        </div>
      ) : (
        <p className="text-[0.8125rem] text-ink-muted">
          Fix the rows listed below in your spreadsheet, then choose the file again and check it.
        </p>
      )}
    </div>
  );
}
